import type { Shot } from "@/lib/work";

type FrameProps = {
  shot: Shot;
  host: string;
  dark?: boolean;
  eager?: boolean;
  className?: string;
};

/**
 * A minimal browser window around a real screenshot. The chrome follows the
 * product: light products get a light bar, dark products a dark one, so the
 * frame reads as part of the product rather than a sticker on top of it.
 */
export function BrowserFrame({ shot, host, dark = false, eager = false, className = "" }: FrameProps) {
  return (
    <div
      className={`overflow-hidden rounded-[14px] shadow-[0_40px_90px_-30px_rgba(10,10,20,0.45)] ring-1 ${
        dark ? "bg-[#0f0f11] ring-white/10" : "bg-white ring-foreground/[0.08]"
      } ${className}`}
    >
      <div
        aria-hidden
        className={`flex h-8 items-center gap-3 px-3.5 ${
          dark ? "bg-[#1b1b1e] text-white/45" : "bg-[#f4f4f5] text-foreground/45"
        }`}
      >
        <span className="flex shrink-0 gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
        </span>
        <span
          className={`mx-auto min-w-0 truncate rounded-md px-3 py-0.5 font-mono text-[10px] tracking-wide ${
            dark ? "bg-white/[0.06]" : "bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.04)]"
          }`}
        >
          {host}
        </span>
        <span className="w-[42px] shrink-0" />
      </div>
      <img
        src={shot.src}
        alt={shot.alt}
        width={shot.w}
        height={shot.h}
        loading={eager ? "eager" : "lazy"}
        fetchPriority={eager ? "high" : undefined}
        decoding="async"
        className="block h-auto w-full rounded-none"
      />
    </div>
  );
}

type PhoneProps = {
  shot: Shot;
  className?: string;
  /** Outer corner radius; scale it with the rendered width. */
  radius?: string;
};

/** A phone bezel around a real mobile screenshot. */
export function PhoneFrame({ shot, className = "", radius = "2.2rem" }: PhoneProps) {
  return (
    <div
      className={`overflow-hidden bg-[#0a0a0b] p-[5px] shadow-[0_40px_80px_-24px_rgba(10,10,20,0.55)] ring-1 ring-black/50 ${className}`}
      style={{ borderRadius: radius }}
    >
      <div className="relative overflow-hidden bg-black" style={{ borderRadius: `calc(${radius} - 5px)` }}>
        <img
          src={shot.src}
          alt={shot.alt}
          width={shot.w}
          height={shot.h}
          loading="lazy"
          decoding="async"
          className="block h-auto w-full rounded-none"
        />
        <span
          aria-hidden
          className="absolute left-1/2 top-[1.6%] h-[2.2%] w-[30%] -translate-x-1/2 rounded-full bg-black"
        />
      </div>
    </div>
  );
}

/** Soft, brand-tinted backdrop shared by tiles and the case-study hero. */
export function tintStyle(accent: string): React.CSSProperties {
  return {
    backgroundImage: `radial-gradient(90% 75% at 100% 0%, ${accent}30, transparent 68%), radial-gradient(70% 60% at 0% 100%, ${accent}16, transparent 62%)`,
  };
}
